import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import { Link, useLocation, useParams } from 'react-router-dom';
import logo from '../assets/LogoHorizontal.png';
import {
  complianceDocumentDescriptions,
  complianceDocumentLabels,
  getComplianceDocumentAccessTier,
  type ComplianceDocumentAccessTier,
  getLegacyRepairManualDocumentType,
} from '../components/products/productComplianceDocuments';
import type { ComplianceDocumentType } from '../components/products/productFormTypes';
import { captureSupabaseError } from '../lib/sentry';

type AccessTier = 'OPEN' | 'RESTRICTED' | 'CONFIDENTIAL';
type AccessRequestRole =
  | 'RECYCLER_REPAIRER'
  | 'AUDITOR_SURVEILLANCE'
  | 'B2B_BUYER'
  | 'OTHER';

type PublicMaterial = {
  materialName: string;
  weightPercentage: number;
  recycledContentPercentage: number | null;
  isRecyclable: boolean;
};

type PublicPackaging = {
  packagingCategory: string;
  materialType: string;
  weightGrams: number;
  pcrContentPercent: number;
  recyclabilityStatus: string | null;
};

type PublicSvhcDeclaration = {
  substanceName: string | null;
  casEcNumber: string | null;
};

type PublicProductDppPayload = {
  productId: string;
  passportId: string;
  product: {
    name: string;
    skuModel: string;
    gtinEan: string | null;
    brandName: string;
    categoryCode: string;
    categoryName: string;
    description: string | null;
    imageUrl: string | null;
  };
  documents: Array<{
    title: string;
    description: string | null;
    category: string | null;
    uploadedAt: string | null;
    url: string | null;
  }>;
  complianceDocuments?: Array<{
    documentType: string;
    url: string | null;
  }>;
  metadata: {
    productWeightValue: number | null;
    productWeightUnit: 'g' | 'kg' | null;
    manufacturerName: string | null;
    manufacturerAddress: string | null;
    importedOutsideEu: boolean;
    importerName: string | null;
    importerEmail: string | null;
    importerAddress: string | null;
    seasonCollection: string | null;
    targetAudience: string[];
    machineClassification: string | null;
    ceResponsiblePerson: string | null;
    ceResponsiblePersonEntityType: string | null;
    ceResponsiblePersonCompanyName: string | null;
    ceResponsiblePersonEuAddress: string | null;
    ceResponsiblePersonContactEmail: string | null;
    includeConsumerPackaging: boolean;
    includeSupplyChainTraceability: boolean;
    lifespanUnit: 'years' | 'months';
    softwareSupportExpiry: string | null;
    energyEfficiencyRating: string | null;
  };
  materials: PublicMaterial[];
  packaging: PublicPackaging[];
  reach: {
    svhcStatus: 'none' | 'contains' | '';
    svhcDeclarations: PublicSvhcDeclaration[];
  };
  rohs: {
    rohsStatus: 'without_exemption' | 'with_exemption' | 'not_applicable' | '';
    exemptionCode?: string | null;
    customExemptionDetails?: string | null;
    exclusionReason?: string | null;
    supportingDocument?: {
      title?: string | null;
      url?: string | null;
    } | null;
  };
  popsPfas: {
    status: 'not_verified' | 'verified_compliant' | 'contains_restricted' | '';
    details?: string | null;
    detectedMaterials?: string[];
    assessmentNote?: string | null;
    supportingDocument?: {
      title?: string | null;
      url?: string | null;
    } | null;
  };
  machineryDeclaration?: {
    kind?: 'doc' | 'doi' | null;
    title?: string | null;
    url?: string | null;
  } | null;
  battery?: {
    ratedCapacityAh?: number | null;
    ratedEnergyKwh?: number | null;
    nominalVoltageV?: number | null;
    minimumVoltageV?: number | null;
    maximumVoltageV?: number | null;
    powerCapabilityLimits?: string | null;
    roundTripEfficiencyPercent?: number | null;
    cycleLifetimeDescriptor?: string | null;
    operatingTemperatureWindow?: string | null;
    extinguishingAgents?: string | null;
    safetyHandlingMeasures?: string | null;
  } | null;
  circularity: {
    carbonFootprint: number | null;
    expectedLifespan: number | null;
    repairabilityScore: number | null;
    repairManual: string | null;
    disassemblyGuide?: string | null;
    microplasticsReport?: {
      title?: string | null;
      url?: string | null;
    } | null;
    recyclingInstructions: string | null;
    waterConsumption: number | null;
    careSymbols: string[];
    colorFastnessRating: number | null;
    pillingResistanceRating: number | null;
    sparePartsAvailability: string | null;
    takeBackScheme: string | null;
    microplasticsStatus: string | null;
  };
  categorySpecific?: {
    construction?: {
      vocEmissionClass?: string | null;
      dopDocument?: {
        title?: string | null;
        url?: string | null;
      } | null;
      demolitionGuide?: {
        title?: string | null;
        url?: string | null;
      } | null;
    } | null;
    furniture?: {
      eudrDdsReference?: string | null;
      flameRetardantsPresent?: boolean;
      flameRetardantChemicalNames?: string[];
      hasRemovableCovers?: boolean | null;
    } | null;
    metals?: {
      productionRoute?: string | null;
      cbamEmbeddedEmissions?: number | null;
    } | null;
    tires?: {
      rollingResistanceClass?: string | null;
      wetGripClass?: string | null;
      abrasionRateMgKm?: number | null;
      isRetreadable?: boolean | null;
    } | null;
    chemicals?: {
      ufiCode?: string | null;
      clpHazardPhrases?: string[];
      surfactantBiodegradabilityPercentage?: number | null;
    } | null;
  } | null;
  passport: {
    status: 'PUBLISHED' | 'ARCHIVED' | 'WITHDRAWN';
    statusReason: string | null;
    statusUpdatedAt: string | null;
    uniqueProductIdentifier: string;
    publicUrl: string | null;
    qrCodeAssetUrl: string | null;
    publishedAt: string | null;
    visibilitySettings?: {
      technicalSpecsAccess?: 'public' | 'restricted';
    };
  };
  access?: {
    grantedTier: AccessTier;
    hasExtendedAccess: boolean;
    hasDocumentAccess?: boolean;
    tokenHolderEmail: string | null;
  };
  extendedData?: {
    traceability: Array<{
      tier: 'tier_1' | 'tier_2' | 'tier_3';
      companyName: string;
      countryCode: string;
      facilityIdOrAddress: string;
      processComponent: string;
    }>;
    compliance: {
      importerEori: string | null;
      hardwareRevision: string | null;
      criticalRawMaterials: boolean;
      criticalRawMaterialsList: string[];
      weeeRegistrationNumber: string | null;
      rohsExemptionCode: string | null;
      customExemptionDetails: string | null;
      rohsExclusionReason: string | null;
    };
  } | null;
};

type AccessRequestFormState = {
  fullName: string;
  email: string;
  organization: string;
  role: AccessRequestRole;
  reason: string;
};

type ComplianceDocumentListItem = {
  key: string;
  title: string;
  description: string;
  url: string | null;
  accessTier: ComplianceDocumentAccessTier;
};

const isComplianceDocumentType = (
  value: string,
): value is ComplianceDocumentType => value in complianceDocumentLabels;

const initialAccessRequestForm: AccessRequestFormState = {
  fullName: '',
  email: '',
  organization: '',
  role: 'AUDITOR_SURVEILLANCE',
  reason: '',
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
};

const normalizePayload = (
  value: PublicProductDppPayload,
): PublicProductDppPayload => ({
  ...value,
  documents: Array.isArray(value.documents)
    ? value.documents.map((document) => ({
        title:
          typeof document?.title === 'string' && document.title.trim()
            ? document.title
            : 'Document',
        description:
          typeof document?.description === 'string'
            ? document.description
            : null,
        category:
          typeof document?.category === 'string' ? document.category : null,
        uploadedAt:
          typeof document?.uploadedAt === 'string' ? document.uploadedAt : null,
        url: typeof document?.url === 'string' ? document.url : null,
      }))
    : [],
  complianceDocuments: Array.isArray(value.complianceDocuments)
    ? value.complianceDocuments
        .map((document) => ({
          documentType:
            typeof document?.documentType === 'string'
              ? document.documentType
              : '',
          url: typeof document?.url === 'string' ? document.url : null,
        }))
        .filter((document) => document.documentType)
    : [],
  metadata: {
    ...value.metadata,
    productWeightValue:
      typeof value.metadata?.productWeightValue === 'number'
        ? value.metadata.productWeightValue
        : null,
    productWeightUnit:
      value.metadata?.productWeightUnit === 'kg' ||
      value.metadata?.productWeightUnit === 'g'
        ? value.metadata.productWeightUnit
        : null,
    importerEmail: value.metadata?.importerEmail ?? null,
    targetAudience: toStringArray(value.metadata?.targetAudience),
    ceResponsiblePersonEntityType:
      value.metadata?.ceResponsiblePersonEntityType ?? null,
    ceResponsiblePersonCompanyName:
      value.metadata?.ceResponsiblePersonCompanyName ?? null,
    ceResponsiblePersonEuAddress:
      value.metadata?.ceResponsiblePersonEuAddress ?? null,
    ceResponsiblePersonContactEmail:
      value.metadata?.ceResponsiblePersonContactEmail ?? null,
  },
  materials: Array.isArray(value.materials) ? value.materials : [],
  packaging: Array.isArray(value.packaging) ? value.packaging : [],
  reach: {
    ...value.reach,
    svhcDeclarations: Array.isArray(value.reach?.svhcDeclarations)
      ? value.reach.svhcDeclarations
      : [],
  },
  circularity: {
    ...value.circularity,
    disassemblyGuide: value.circularity?.disassemblyGuide ?? null,
    microplasticsReport: value.circularity?.microplasticsReport
      ? {
          title: value.circularity.microplasticsReport.title ?? null,
          url: value.circularity.microplasticsReport.url ?? null,
        }
      : null,
    careSymbols: toStringArray(value.circularity?.careSymbols),
  },
  rohs: {
    ...value.rohs,
    exemptionCode: value.rohs?.exemptionCode ?? null,
    customExemptionDetails: value.rohs?.customExemptionDetails ?? null,
    exclusionReason: value.rohs?.exclusionReason ?? null,
    supportingDocument: value.rohs?.supportingDocument
      ? {
          title: value.rohs.supportingDocument.title ?? null,
          url: value.rohs.supportingDocument.url ?? null,
        }
      : null,
  },
  popsPfas: {
    ...value.popsPfas,
    details: value.popsPfas?.details ?? null,
    detectedMaterials: toStringArray(value.popsPfas?.detectedMaterials),
    assessmentNote: value.popsPfas?.assessmentNote ?? null,
    supportingDocument: value.popsPfas?.supportingDocument
      ? {
          title: value.popsPfas.supportingDocument.title ?? null,
          url: value.popsPfas.supportingDocument.url ?? null,
        }
      : null,
  },
  machineryDeclaration: value.machineryDeclaration
    ? {
        kind: value.machineryDeclaration.kind ?? null,
        title: value.machineryDeclaration.title ?? null,
        url: value.machineryDeclaration.url ?? null,
      }
    : null,
  battery: value.battery
    ? {
        ratedCapacityAh: value.battery.ratedCapacityAh ?? null,
        ratedEnergyKwh: value.battery.ratedEnergyKwh ?? null,
        nominalVoltageV: value.battery.nominalVoltageV ?? null,
        minimumVoltageV: value.battery.minimumVoltageV ?? null,
        maximumVoltageV: value.battery.maximumVoltageV ?? null,
        powerCapabilityLimits: value.battery.powerCapabilityLimits ?? null,
        roundTripEfficiencyPercent:
          value.battery.roundTripEfficiencyPercent ?? null,
        cycleLifetimeDescriptor: value.battery.cycleLifetimeDescriptor ?? null,
        operatingTemperatureWindow:
          value.battery.operatingTemperatureWindow ?? null,
        extinguishingAgents: value.battery.extinguishingAgents ?? null,
        safetyHandlingMeasures: value.battery.safetyHandlingMeasures ?? null,
      }
    : null,
  categorySpecific: {
    construction: value.categorySpecific?.construction
      ? {
          vocEmissionClass:
            value.categorySpecific.construction.vocEmissionClass ?? null,
          dopDocument: value.categorySpecific.construction.dopDocument
            ? {
                title:
                  value.categorySpecific.construction.dopDocument.title ?? null,
                url:
                  value.categorySpecific.construction.dopDocument.url ?? null,
              }
            : null,
          demolitionGuide: value.categorySpecific.construction.demolitionGuide
            ? {
                title:
                  value.categorySpecific.construction.demolitionGuide.title ??
                  null,
                url:
                  value.categorySpecific.construction.demolitionGuide.url ??
                  null,
              }
            : null,
        }
      : null,
    furniture: value.categorySpecific?.furniture
      ? {
          eudrDdsReference:
            value.categorySpecific.furniture.eudrDdsReference ?? null,
          flameRetardantsPresent:
            value.categorySpecific.furniture.flameRetardantsPresent ?? false,
          flameRetardantChemicalNames: toStringArray(
            value.categorySpecific.furniture.flameRetardantChemicalNames,
          ),
          hasRemovableCovers:
            value.categorySpecific.furniture.hasRemovableCovers ?? null,
        }
      : null,
    metals: value.categorySpecific?.metals
      ? {
          productionRoute:
            value.categorySpecific.metals.productionRoute ?? null,
          cbamEmbeddedEmissions:
            value.categorySpecific.metals.cbamEmbeddedEmissions ?? null,
        }
      : null,
    tires: value.categorySpecific?.tires
      ? {
          rollingResistanceClass:
            value.categorySpecific.tires.rollingResistanceClass ?? null,
          wetGripClass: value.categorySpecific.tires.wetGripClass ?? null,
          abrasionRateMgKm:
            value.categorySpecific.tires.abrasionRateMgKm ?? null,
          isRetreadable: value.categorySpecific.tires.isRetreadable ?? null,
        }
      : null,
    chemicals: value.categorySpecific?.chemicals
      ? {
          ufiCode: value.categorySpecific.chemicals.ufiCode ?? null,
          clpHazardPhrases: toStringArray(
            value.categorySpecific.chemicals.clpHazardPhrases,
          ),
          surfactantBiodegradabilityPercentage:
            value.categorySpecific.chemicals
              .surfactantBiodegradabilityPercentage ?? null,
        }
      : null,
  },
  passport: {
    ...value.passport,
    visibilitySettings: {
      technicalSpecsAccess:
        value.passport?.visibilitySettings?.technicalSpecsAccess ===
        'restricted'
          ? ('restricted' as const)
          : ('public' as const),
    },
  },
});

const formatProductWeight = (
  value: number | null | undefined,
  unit: 'g' | 'kg' | null | undefined,
) => {
  if (value == null || Number.isNaN(value)) return null;

  const formattedValue = Number.isInteger(value)
    ? String(value)
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 });

  return `${formattedValue} ${unit === 'kg' ? 'kg' : 'g'}`;
};

const formatNumber = (value: number | null | undefined, suffix = '') => {
  if (value === null || value === undefined) return '-';
  return `${Number(value).toLocaleString()}${suffix}`;
};

const formatPercent = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '-';
  return `${Number(value).toFixed(2)}%`;
};

const formatDate = (value: string | null | undefined) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString();
};

const titleCase = (value: string | null | undefined) => {
  if (!value) return '-';
  return value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const getPackagingCategoryLabel = (value: string | null | undefined) => {
  switch (value) {
    case 'primary':
      return 'Primary / Sales Packaging';
    case 'secondary':
      return 'Secondary / Grouped Packaging';
    case 'tertiary':
    case 'ecommerce':
      return 'Tertiary / Transport Packaging';
    default:
      return titleCase(value);
  }
};

const hasText = (value: string | null | undefined) =>
  typeof value === 'string' && value.trim().length > 0;

const getMachineClassificationLabel = (value: string | null | undefined) => {
  switch (value) {
    case 'STANDARD_MACHINERY':
      return 'Standard Machinery - Self-Declaration';
    case 'HIGH_RISK_MACHINERY':
      return 'High-Risk Machinery - Annex I Notified Body Required';
    case 'PARTLY_COMPLETED_MACHINERY':
    case 'PARTLY_COMPLETED':
      return 'Partly Completed Machinery - Declaration of Incorporation';
    default:
      return value;
  }
};

const buildComplianceDocuments = (
  payload: PublicProductDppPayload,
): ComplianceDocumentListItem[] => {
  if (payload.complianceDocuments?.length) {
    const seen = new Set<string>();

    return payload.complianceDocuments.flatMap((document) => {
      const { documentType } = document;

      if (
        !documentType ||
        !isComplianceDocumentType(documentType) ||
        seen.has(documentType)
      ) {
        return [];
      }

      seen.add(documentType);

      return [
        {
          key: `compliance-${documentType}`,
          title: complianceDocumentLabels[documentType],
          description: complianceDocumentDescriptions[documentType],
          url: document.url ?? null,
          accessTier: getComplianceDocumentAccessTier(documentType),
        },
      ];
    });
  }

  const documents: ComplianceDocumentListItem[] = [];

  if (payload.machineryDeclaration) {
    const documentType =
      payload.machineryDeclaration.kind === 'doi' ? 'DOI' : 'DOC';

    documents.push({
      key: `machinery-${documentType}`,
      title: complianceDocumentLabels[documentType],
      description: complianceDocumentDescriptions[documentType],
      url: payload.machineryDeclaration.url ?? null,
      accessTier: getComplianceDocumentAccessTier(documentType),
    });
  }

  if (payload.circularity.repairManual) {
    const documentType = getLegacyRepairManualDocumentType(
      payload.product.categoryCode,
    );

    documents.push({
      key: `repair-${documentType}`,
      title: complianceDocumentLabels[documentType],
      description: complianceDocumentDescriptions[documentType],
      url: payload.circularity.repairManual,
      accessTier: getComplianceDocumentAccessTier(documentType),
    });
  }

  if (payload.circularity.disassemblyGuide) {
    documents.push({
      key: 'disassembly-guide',
      title: complianceDocumentLabels.DISASSEMBLY_GUIDE,
      description: complianceDocumentDescriptions.DISASSEMBLY_GUIDE,
      url: payload.circularity.disassemblyGuide,
      accessTier: getComplianceDocumentAccessTier('DISASSEMBLY_GUIDE'),
    });
  }

  if (payload.categorySpecific?.construction?.dopDocument) {
    documents.push({
      key: 'construction-dop',
      title: complianceDocumentLabels.DOP,
      description: complianceDocumentDescriptions.DOP,
      url: payload.categorySpecific.construction.dopDocument.url ?? null,
      accessTier: getComplianceDocumentAccessTier('DOP'),
    });
  }

  if (payload.categorySpecific?.construction?.demolitionGuide) {
    documents.push({
      key: 'construction-demolition-guide',
      title: complianceDocumentLabels.DEMOLITION_GUIDE,
      description: complianceDocumentDescriptions.DEMOLITION_GUIDE,
      url: payload.categorySpecific.construction.demolitionGuide.url ?? null,
      accessTier: getComplianceDocumentAccessTier('DEMOLITION_GUIDE'),
    });
  }

  return documents;
};

const getCeResponsibleEntityTypeLabel = (value: string | null | undefined) => {
  switch (value) {
    case 'EU_MANUFACTURER':
      return 'EU Manufacturer';
    case 'EU_IMPORTER':
      return 'EU Importer';
    case 'AUTHORIZED_REPRESENTATIVE':
      return 'Authorized Representative';
    default:
      return value;
  }
};

const hasNumber = (value: number | null | undefined) =>
  value !== null && value !== undefined;

const getPublicPassportBadgeVariant = (
  status: PublicProductDppPayload['passport']['status'],
) => {
  switch (status) {
    case 'ARCHIVED':
      return 'warning';
    case 'WITHDRAWN':
      return 'danger';
    default:
      return 'success';
  }
};

const getRohsStatusLabel = (
  status: PublicProductDppPayload['rohs']['rohsStatus'],
) => {
  switch (status) {
    case 'without_exemption':
      return 'Compliant without exemption';
    case 'with_exemption':
      return 'Compliant with exemption';
    case 'not_applicable':
      return 'Not applicable';
    default:
      return 'Not declared';
  }
};

const getPopsPfasStatusLabel = (
  status: PublicProductDppPayload['popsPfas']['status'],
) => {
  switch (status) {
    case 'verified_compliant':
      return 'Verified compliant';
    case 'contains_restricted':
      return 'Contains restricted substances / exemption applies';
    case 'not_verified':
      return 'Not verified yet';
    default:
      return 'Not declared';
  }
};

const getBatteryPfasStatusLabel = (
  popsPfas: PublicProductDppPayload['popsPfas'],
) => {
  const detectedMaterials = popsPfas.detectedMaterials ?? [];

  if (popsPfas.status === 'contains_restricted') {
    const details = popsPfas.details || detectedMaterials.join(', ');
    return details ? `Contains ${details}` : 'Declared';
  }

  if (popsPfas.status === 'verified_compliant') {
    return 'Declared / Not Present above threshold';
  }

  if (detectedMaterials.length > 0) {
    return `Contains ${detectedMaterials.join(', ')}`;
  }

  return 'Not Present above threshold';
};

export default function PublicProductDpp() {
  const location = useLocation();
  const { productId, gtin, serial, batch } = useParams<{
    productId?: string;
    gtin?: string;
    serial?: string;
    batch?: string;
  }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<PublicProductDppPayload | null>(null);
  const [showAccessRequestModal, setShowAccessRequestModal] = useState(false);
  const [accessRequestSubmitting, setAccessRequestSubmitting] = useState(false);
  const [accessRequestError, setAccessRequestError] = useState<string | null>(
    null,
  );
  const [accessRequestSuccess, setAccessRequestSuccess] = useState<
    string | null
  >(null);
  const [accessRequestForm, setAccessRequestForm] =
    useState<AccessRequestFormState>(initialAccessRequestForm);

  const accessToken = useMemo(
    () => new URLSearchParams(location.search).get('token'),
    [location.search],
  );

  useEffect(() => {
    const loadPayload = async () => {
      if (!productId && !gtin) {
        setError('Product identifier is missing.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-public-product-dpp`;

      try {
        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify(
            productId
              ? {
                  product_id: productId,
                  access_token: accessToken,
                }
              : {
                  gtin,
                  serial: serial ?? null,
                  batch: batch ?? null,
                  access_token: accessToken,
                },
          ),
        });

        const data = (await response.json()) as {
          data?: PublicProductDppPayload | null;
          error?: string;
        };

        if (!response.ok) {
          const message = data.error ?? 'Unable to load this product passport.';
          captureSupabaseError(new Error(message), {
            op: 'public-dpp.fetch',
            productId: productId ?? null,
            gtin: gtin ?? null,
            serial: serial ?? null,
            batch: batch ?? null,
            status: response.status,
          });
          setError(message);
          setPayload(null);
          return;
        }

        if (!data?.data) {
          setPayload(null);
          return;
        }

        setPayload(normalizePayload(data.data as PublicProductDppPayload));
      } catch (loadError) {
        captureSupabaseError(loadError, {
          op: 'public-dpp.fetch',
          productId: productId ?? null,
          gtin: gtin ?? null,
          serial: serial ?? null,
          batch: batch ?? null,
        });
        setError('Unable to load this product passport.');
        setPayload(null);
      } finally {
        setLoading(false);
      }
    };

    void loadPayload();
  }, [accessToken, batch, gtin, productId, serial]);

  const targetAudience = useMemo(
    () => payload?.metadata.targetAudience.join(', ') ?? '',
    [payload],
  );
  const ceResponsiblePersonSummary = useMemo(() => {
    const values = [
      payload?.metadata.ceResponsiblePersonCompanyName,
      payload?.metadata.ceResponsiblePersonEuAddress,
    ].filter((value): value is string => hasText(value));

    return values.join(', ');
  }, [payload]);
  const passportStatus = payload?.passport.status ?? 'PUBLISHED';
  const isArchived = passportStatus === 'ARCHIVED';
  const isWithdrawn = passportStatus === 'WITHDRAWN';
  const grantedTier = payload?.access?.grantedTier ?? 'OPEN';
  const isConfidentialTier = grantedTier === 'CONFIDENTIAL';
  const hasExtendedAccess = payload?.access?.hasExtendedAccess ?? false;
  const technicalSpecsAccess =
    payload?.passport.visibilitySettings?.technicalSpecsAccess ?? 'public';
  const technicalSpecsVisible =
    technicalSpecsAccess === 'public' || hasExtendedAccess;
  const isBatteryProduct =
    payload?.product.categoryCode === 'BATTERIES_STORAGE';
  const isConstructionProduct =
    payload?.product.categoryCode === 'CONSTRUCTION_PRODUCTS';
  const isFurnitureProduct =
    payload?.product.categoryCode === 'FURNITURE_MATTRESSES';
  const isMetalsProduct = payload?.product.categoryCode === 'IRON_STEEL_METALS';
  const isTiresProduct = payload?.product.categoryCode === 'TIRES_AUTOMOTIVE';
  const isChemicalsProduct = payload?.product.categoryCode === 'CHEMICALS';
  const popsPfasAppliesToProduct =
    payload?.product.categoryCode === 'TEXTILES_APPAREL' || isBatteryProduct;
  const productWeight = formatProductWeight(
    payload?.metadata.productWeightValue,
    payload?.metadata.productWeightUnit,
  );
  const complianceDocuments = payload ? buildComplianceDocuments(payload) : [];
  const publicDocuments = payload?.documents ?? [];
  const publicPassportQrValue = useMemo(() => {
    if (payload?.passport.publicUrl) {
      return payload.passport.publicUrl;
    }

    if (typeof window === 'undefined') {
      return null;
    }

    return `${window.location.origin}${location.pathname}${location.search}`;
  }, [location.pathname, location.search, payload?.passport.publicUrl]);

  const updateAccessRequestForm = <Key extends keyof AccessRequestFormState>(
    key: Key,
    value: AccessRequestFormState[Key],
  ) => {
    setAccessRequestForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const closeAccessRequestModal = () => {
    if (accessRequestSubmitting) return;
    setShowAccessRequestModal(false);
    setAccessRequestError(null);
    setAccessRequestForm(initialAccessRequestForm);
  };

  const handleSubmitAccessRequest = async () => {
    if (!payload?.passportId) return;

    const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/request-dpp-access`;
    setAccessRequestSubmitting(true);
    setAccessRequestError(null);

    try {
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          passportId: payload.passportId,
          fullName: accessRequestForm.fullName.trim(),
          email: accessRequestForm.email.trim(),
          organization: accessRequestForm.organization.trim(),
          role: accessRequestForm.role,
          reason: accessRequestForm.reason.trim(),
        }),
      });

      const data = (await response.json()) as { error?: string };
      if (!response.ok) {
        throw new Error(data.error ?? 'Unable to submit access request.');
      }

      setAccessRequestSuccess(
        'Access request submitted. The brand administrator has been notified. You will receive a Link via email upon approval.',
      );
      setShowAccessRequestModal(false);
      setAccessRequestForm(initialAccessRequestForm);
    } catch (submitError) {
      setAccessRequestError(
        submitError instanceof Error
          ? submitError.message
          : 'Unable to submit access request.',
      );
    } finally {
      setAccessRequestSubmitting(false);
    }
  };

  useEffect(() => {
    if (payload) {
      document.title = `${payload.product.name} ${payload.product.skuModel} - SupplyPassport Digital Product Passport`;
      return;
    }

    if (loading) {
      document.title = 'Loading Digital Product Passport - SupplyPassport';
      return;
    }

    if (error) {
      document.title = 'Digital Product Passport Unavailable - SupplyPassport';
      return;
    }

    document.title = 'SupplyPassport Digital Product Passport';
  }, [error, loading, payload]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <Container className="py-5" style={{ maxWidth: '960px' }}>
        <Card className="shadow-sm border-0">
          <Card.Body className="p-4 p-lg-5">
            <img src={logo} alt="SupplyPassport" height="34" className="mb-4" />
            <h1 className="h3 mb-3">Unable to load this product passport</h1>
            <p className="text-muted mb-0">{error}</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (!payload) {
    return (
      <Container className="py-5" style={{ maxWidth: '960px' }}>
        <Card className="shadow-sm border-0">
          <Card.Body className="p-4 p-lg-5">
            <img src={logo} alt="SupplyPassport" height="34" className="mb-4" />
            <h1 className="h3 mb-3">Product passport unavailable</h1>
            <p className="text-muted mb-0">
              This product is not publicly available yet, or the link is
              invalid.
            </p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-4 py-lg-5" style={{ maxWidth: '1120px' }}>
      <style>
        {`
          .public-dpp-hero {
            background: linear-gradient(135deg, #f7f4ec 0%, #ffffff 62%, #edf6f2 100%);
            border: 1px solid rgba(28, 52, 43, 0.08);
            border-radius: 1.5rem;
            overflow: hidden;
          }

          .public-dpp-hero-content,
          .public-dpp-hero-copy,
          .public-dpp-hero-meta,
          .public-dpp-hero-side {
            min-width: 0;
          }

          .public-dpp-hero-copy {
            flex: 1 1 auto;
          }

          .public-dpp-hero-meta {
            overflow-wrap: anywhere;
            word-break: break-word;
          }

          .public-dpp-hero-identifiers {
            overflow-wrap: anywhere;
            word-break: break-word;
          }

          .public-dpp-hero-identifiers > span {
            min-width: 0;
          }

          .public-dpp-card {
            border-radius: 1.25rem;
            border: 1px solid rgba(28, 52, 43, 0.08);
          }

          .public-dpp-image {
            width: 112px;
            height: 112px;
            border-radius: 1.5rem;
            object-fit: cover;
            background: linear-gradient(135deg, #f0ede4 0%, #dfece5 100%);
            border: 1px solid rgba(28, 52, 43, 0.08);
          }

          .public-dpp-image-placeholder {
            display: flex;
            align-items: center;
            justify-content: center;
            color: #385246;
            font-size: 2rem;
          }

          @media (max-width: 991.98px) {
            .public-dpp-hero-side {
              align-self: center;
              text-align: center;
            }
          }

          @media (max-width: 575.98px) {
            .public-dpp-hero-copy {
              flex-direction: column;
            }

            .public-dpp-image {
              width: 96px;
              height: 96px;
            }
          }
        `}
      </style>

      <div className="d-flex align-items-center justify-content-between gap-3 mb-4">
        <Link
          to="/"
          className="text-decoration-none text-dark d-inline-flex align-items-center gap-2"
        >
          <img src={logo} alt="SupplyPassport" height="30" />
        </Link>
        <div className="d-flex align-items-center gap-2">
          <Badge bg={hasExtendedAccess ? 'info' : 'success'}>
            {hasExtendedAccess ? `${grantedTier} Access` : 'Public DPP'}
          </Badge>
        </div>
      </div>

      {accessRequestSuccess && (
        <Alert variant="success" className="mb-4">
          {accessRequestSuccess}
        </Alert>
      )}

      {isArchived && (
        <Alert variant="warning" className="mb-4">
          This passport has been archived. The information below reflects the
          last published version and may no longer represent an active product.
          {payload.passport.statusReason
            ? ` Reason: ${payload.passport.statusReason}`
            : ''}
        </Alert>
      )}

      {isWithdrawn && (
        <Alert variant="danger" className="mb-4">
          This passport has been withdrawn and its technical specifications are
          no longer publicly available.
          {payload.passport.statusReason
            ? ` Reason: ${payload.passport.statusReason}`
            : ''}
        </Alert>
      )}

      <Card className="public-dpp-hero shadow-sm mb-4">
        <Card.Body className="p-4 p-lg-5">
          <div className="public-dpp-hero-content d-flex flex-column flex-lg-row justify-content-between gap-4">
            <div className="public-dpp-hero-copy d-flex gap-3 align-items-start">
              {payload.product.imageUrl ? (
                <img
                  src={payload.product.imageUrl ?? undefined}
                  alt={payload.product.name}
                  className="public-dpp-image"
                />
              ) : (
                <div className="public-dpp-image public-dpp-image-placeholder">
                  <i className="bi bi-box-seam" />
                </div>
              )}
              <div className="public-dpp-hero-meta">
                <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                  <h1 className="h2 mb-0">{payload.product.name}</h1>
                  <Badge bg="light" text="dark" className="border">
                    {payload.product.categoryName ||
                      payload.product.categoryCode}
                  </Badge>
                  <Badge
                    bg={getPublicPassportBadgeVariant(payload.passport.status)}
                  >
                    {payload.passport.status}
                  </Badge>
                  {payload.machineryDeclaration && (
                    <Badge
                      bg={
                        payload.machineryDeclaration.kind === 'doi'
                          ? 'light'
                          : 'success'
                      }
                      text={
                        payload.machineryDeclaration.kind === 'doi'
                          ? 'dark'
                          : undefined
                      }
                      className={
                        payload.machineryDeclaration.kind === 'doi'
                          ? 'border'
                          : undefined
                      }
                    >
                      <i className="bi bi-check-lg me-1" />
                      {payload.machineryDeclaration.kind === 'doi'
                        ? 'Declaration of Incorporation Present'
                        : 'CE Declaration Present'}
                    </Badge>
                  )}
                </div>
                <div className="text-muted mb-2">
                  {payload.product.brandName}
                </div>
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2 small">
                  <span className="text-muted">
                    {isArchived || isWithdrawn ? 'Status Updated' : 'Published'}
                  </span>
                  <span className="fw-semibold text-body">
                    {formatDate(
                      payload.passport.statusUpdatedAt ??
                        payload.passport.publishedAt,
                    )}
                  </span>
                </div>
                <div className="public-dpp-hero-identifiers small text-muted d-flex flex-wrap gap-3">
                  <span>SKU / Model: {payload.product.skuModel}</span>
                  <span>GTIN / EAN: {payload.product.gtinEan || '-'}</span>
                  <span>UPI: {payload.passport.uniqueProductIdentifier}</span>
                  {productWeight && <span>Weight: {productWeight}</span>}
                </div>
              </div>
            </div>
            <div className="public-dpp-hero-side text-lg-end">
              {publicPassportQrValue && (
                <div className="mb-3 d-inline-flex flex-column align-items-center align-items-lg-end">
                  <div className="bg-white border rounded p-2 shadow-sm">
                    <QRCodeSVG
                      value={publicPassportQrValue}
                      size={112}
                      level="H"
                      includeMargin
                    />
                  </div>
                </div>
              )}
              {/* {technicalSpecsVisible &&
                (payload.circularity.repairManual ||
                  payload.circularity.disassemblyGuide) && (
                  <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                    {payload.circularity.repairManual && (
                      <a
                        href={payload.circularity.repairManual}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-outline-dark btn-sm"
                      >
                        Open Repair Manual
                      </a>
                    )}
                    {payload.circularity.disassemblyGuide && (
                      <a
                        href={payload.circularity.disassemblyGuide}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-outline-dark btn-sm"
                      >
                        Open Disassembly Instructions
                      </a>
                    )}
                  </div>
                )} */}
            </div>
          </div>
          {payload.product.description && (
            <p className="text-muted mt-4 mb-0">
              {payload.product.description}
            </p>
          )}
        </Card.Body>
      </Card>

      {isWithdrawn ? (
        <Card className="public-dpp-card shadow-sm">
          <Card.Body className="p-4 p-lg-5">
            <div className="text-uppercase small fw-semibold text-muted mb-3">
              Withdrawal Notice
            </div>
            <h2 className="h4 mb-3">Public specifications removed</h2>
            <p className="text-muted mb-3">
              This Digital Product Passport has been withdrawn. Detailed
              materials, compliance, and circularity specifications are
              intentionally hidden from the public route.
            </p>
            <dl className="row mb-0">
              <dt className="col-sm-4">Product</dt>
              <dd className="col-sm-8">{payload.product.name}</dd>
              <dt className="col-sm-4">Brand</dt>
              <dd className="col-sm-8">{payload.product.brandName}</dd>
              <dt className="col-sm-4">SKU / Model</dt>
              <dd className="col-sm-8">{payload.product.skuModel}</dd>
              <dt className="col-sm-4">Reason</dt>
              <dd className="col-sm-8">
                {payload.passport.statusReason || '-'}
              </dd>
            </dl>
          </Card.Body>
        </Card>
      ) : (
        <Row className="g-4">
          <Col xs={12}>
            <Card className="public-dpp-card shadow-sm h-100">
              <Card.Body className="p-4">
                <div className="text-uppercase small fw-semibold text-muted mb-3">
                  Economic Operator
                </div>
                <dl className="row mb-0">
                  {hasText(payload.metadata.manufacturerName) && (
                    <>
                      <dt className="col-sm-5">Manufacturer</dt>
                      <dd className="col-sm-7">
                        {payload.metadata.manufacturerName}
                      </dd>
                    </>
                  )}
                  {hasText(payload.metadata.manufacturerAddress) && (
                    <>
                      <dt className="col-sm-5">Manufacturer Address</dt>
                      <dd className="col-sm-7">
                        {payload.metadata.manufacturerAddress}
                      </dd>
                    </>
                  )}
                  <dt className="col-sm-5">Imported Outside EU</dt>
                  <dd className="col-sm-7">
                    {payload.metadata.importedOutsideEu ? 'Yes' : 'No'}
                  </dd>
                  {hasText(payload.metadata.importerName) && (
                    <>
                      <dt className="col-sm-5">EU Importer</dt>
                      <dd className="col-sm-7">
                        {payload.metadata.importerName}
                      </dd>
                    </>
                  )}
                  {hasText(payload.metadata.importerEmail) && (
                    <>
                      <dt className="col-sm-5">Importer Email</dt>
                      <dd className="col-sm-7">
                        {payload.metadata.importerEmail}
                      </dd>
                    </>
                  )}
                  {hasText(payload.metadata.importerAddress) && (
                    <>
                      <dt className="col-sm-5">Importer Address</dt>
                      <dd className="col-sm-7">
                        {payload.metadata.importerAddress}
                      </dd>
                    </>
                  )}
                  {hasText(payload.metadata.seasonCollection) && (
                    <>
                      <dt className="col-sm-5">Season / Collection</dt>
                      <dd className="col-sm-7">
                        {payload.metadata.seasonCollection}
                      </dd>
                    </>
                  )}
                  {hasText(targetAudience) && (
                    <>
                      <dt className="col-sm-5">Target Audience</dt>
                      <dd className="col-sm-7">{targetAudience}</dd>
                    </>
                  )}
                  {hasText(payload.metadata.machineClassification) && (
                    <>
                      <dt className="col-sm-5">Machine Classification</dt>
                      <dd className="col-sm-7">
                        {getMachineClassificationLabel(
                          payload.metadata.machineClassification,
                        )}
                      </dd>
                    </>
                  )}
                  {(hasText(ceResponsiblePersonSummary) ||
                    hasText(payload.metadata.ceResponsiblePerson)) && (
                    <>
                      <dt className="col-sm-5">CE Responsible Person</dt>
                      <dd className="col-sm-7">
                        {ceResponsiblePersonSummary ||
                          payload.metadata.ceResponsiblePerson}
                      </dd>
                    </>
                  )}
                  {hasText(payload.metadata.ceResponsiblePersonEntityType) && (
                    <>
                      <dt className="col-sm-5">CE Responsible Person Type</dt>
                      <dd className="col-sm-7">
                        {getCeResponsibleEntityTypeLabel(
                          payload.metadata.ceResponsiblePersonEntityType,
                        )}
                      </dd>
                    </>
                  )}
                  {hasText(payload.metadata.ceResponsiblePersonEuAddress) && (
                    <>
                      <dt className="col-sm-5">
                        CE Responsible Person Address
                      </dt>
                      <dd className="col-sm-7">
                        {payload.metadata.ceResponsiblePersonEuAddress}
                      </dd>
                    </>
                  )}
                  {hasText(
                    payload.metadata.ceResponsiblePersonContactEmail,
                  ) && (
                    <>
                      <dt className="col-sm-5">
                        CE Responsible Person Contact Email
                      </dt>
                      <dd className="col-sm-7">
                        {payload.metadata.ceResponsiblePersonContactEmail}
                      </dd>
                    </>
                  )}
                </dl>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12}>
            <Card className="public-dpp-card shadow-sm h-100">
              <Card.Body className="p-4">
                <div className="text-uppercase small fw-semibold text-muted mb-3">
                  Circularity
                </div>
                {technicalSpecsVisible ? (
                  <>
                    <dl className="row mb-0">
                      {hasNumber(payload.circularity.carbonFootprint) && (
                        <>
                          <dt className="col-sm-6">Carbon Footprint</dt>
                          <dd className="col-sm-6">
                            {formatNumber(
                              payload.circularity.carbonFootprint,
                              ' kg CO2e',
                            )}
                          </dd>
                        </>
                      )}
                      {hasNumber(payload.circularity.expectedLifespan) && (
                        <>
                          <dt className="col-sm-6">Expected Lifespan</dt>
                          <dd className="col-sm-6">
                            {`${payload.circularity.expectedLifespan} ${payload.metadata.lifespanUnit}`}
                          </dd>
                        </>
                      )}
                      {hasNumber(payload.circularity.repairabilityScore) && (
                        <>
                          <dt className="col-sm-6">Repairability Score</dt>
                          <dd className="col-sm-6">
                            {formatNumber(
                              payload.circularity.repairabilityScore,
                              ' / 10',
                            )}
                          </dd>
                        </>
                      )}
                      {hasNumber(payload.circularity.waterConsumption) && (
                        <>
                          <dt className="col-sm-6">Water Consumption</dt>
                          <dd className="col-sm-6">
                            {formatNumber(
                              payload.circularity.waterConsumption,
                              ' L',
                            )}
                          </dd>
                        </>
                      )}
                      {hasNumber(payload.circularity.colorFastnessRating) && (
                        <>
                          <dt className="col-sm-6">Color Fastness</dt>
                          <dd className="col-sm-6">
                            {formatNumber(
                              payload.circularity.colorFastnessRating,
                            )}
                          </dd>
                        </>
                      )}
                      {hasNumber(
                        payload.circularity.pillingResistanceRating,
                      ) && (
                        <>
                          <dt className="col-sm-6">Pilling Resistance</dt>
                          <dd className="col-sm-6">
                            {formatNumber(
                              payload.circularity.pillingResistanceRating,
                            )}
                          </dd>
                        </>
                      )}
                      {hasText(payload.circularity.sparePartsAvailability) && (
                        <>
                          <dt className="col-sm-6">Spare Parts Availability</dt>
                          <dd className="col-sm-6">
                            {payload.circularity.sparePartsAvailability}
                          </dd>
                        </>
                      )}
                      {hasText(payload.circularity.takeBackScheme) && (
                        <>
                          <dt className="col-sm-6">Take-Back Scheme</dt>
                          <dd className="col-sm-6">
                            {payload.circularity.takeBackScheme}
                          </dd>
                        </>
                      )}
                      {hasText(payload.circularity.microplasticsStatus) && (
                        <>
                          <dt className="col-sm-6">Microplastics Status</dt>
                          <dd className="col-sm-6">
                            {titleCase(payload.circularity.microplasticsStatus)}
                          </dd>
                        </>
                      )}
                      {payload.circularity.microplasticsReport?.url && (
                        <>
                          <dt className="col-sm-6">
                            Microplastics Shedding Report
                          </dt>
                          <dd className="col-sm-6">
                            <a
                              href={payload.circularity.microplasticsReport.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {payload.circularity.microplasticsReport.title ||
                                'Open Microplastics Shedding Report'}
                            </a>
                          </dd>
                        </>
                      )}
                      {hasText(payload.metadata.softwareSupportExpiry) && (
                        <>
                          <dt className="col-sm-6">Software Support Expiry</dt>
                          <dd className="col-sm-6">
                            {formatDate(payload.metadata.softwareSupportExpiry)}
                          </dd>
                        </>
                      )}
                      {hasText(payload.metadata.energyEfficiencyRating) && (
                        <>
                          <dt className="col-sm-6">Energy Efficiency Rating</dt>
                          <dd className="col-sm-6">
                            {payload.metadata.energyEfficiencyRating}
                          </dd>
                        </>
                      )}
                    </dl>
                    <div className="mt-3">
                      <div className="small text-muted mb-1">
                        Recycling Instructions
                      </div>
                      <div>
                        {payload.circularity.recyclingInstructions ||
                          'No recycling instructions published.'}
                      </div>
                    </div>
                    {payload.circularity.careSymbols.length > 0 && (
                      <div className="mt-3">
                        <div className="small text-muted mb-2">
                          Care Symbols
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {payload.circularity.careSymbols.map((symbol) => (
                            <Badge
                              key={symbol}
                              bg="light"
                              text="dark"
                              className="border"
                            >
                              {symbol}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <Alert variant="secondary" className="mb-0">
                    Technical specifications for this product require restricted
                    access.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>

          {technicalSpecsVisible ? (
            <>
              <Col xs={12}>
                <Card className="public-dpp-card shadow-sm">
                  <Card.Body className="p-4">
                    <div className="text-uppercase small fw-semibold text-muted mb-3">
                      Materials
                    </div>
                    {payload.materials.length > 0 ? (
                      <Table responsive className="mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>Material</th>
                            <th>Weight %</th>
                            <th>Recycled Content</th>
                            <th>Recyclable</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payload.materials.map((material) => (
                            <tr
                              key={`${material.materialName}-${material.weightPercentage}`}
                            >
                              <td>{material.materialName}</td>
                              <td>
                                {formatPercent(material.weightPercentage)}
                              </td>
                              <td>
                                {formatPercent(
                                  material.recycledContentPercentage,
                                )}
                              </td>
                              <td>{material.isRecyclable ? 'Yes' : 'No'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <div className="text-muted">
                        No material data available.
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12}>
                <Card className="public-dpp-card shadow-sm">
                  <Card.Body className="p-4">
                    <div className="d-flex flex-wrap justify-content-between gap-3 mb-3">
                      <div className="text-uppercase small fw-semibold text-muted">
                        Packaging
                      </div>
                      <Badge
                        bg={
                          payload.metadata.includeConsumerPackaging
                            ? 'success'
                            : 'secondary'
                        }
                      >
                        {payload.metadata.includeConsumerPackaging
                          ? 'Included'
                          : 'No packaging'}
                      </Badge>
                    </div>
                    {payload.packaging.length > 0 ? (
                      <Table responsive className="mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>Category</th>
                            <th>Material</th>
                            <th>Weight (g)</th>
                            <th>PCR %</th>
                            <th>Recyclability</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payload.packaging.map((item, index) => (
                            <tr key={`${item.packagingCategory}-${index}`}>
                              <td>
                                {getPackagingCategoryLabel(
                                  item.packagingCategory,
                                )}
                              </td>
                              <td>{titleCase(item.materialType)}</td>
                              <td>{formatNumber(item.weightGrams)}</td>
                              <td>{formatPercent(item.pcrContentPercent)}</td>
                              <td>{titleCase(item.recyclabilityStatus)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <div className="text-muted">
                        {payload.metadata.includeConsumerPackaging
                          ? 'No packaging data available.'
                          : 'Item comes with no packaging.'}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {isBatteryProduct && payload.battery && (
                <Col xs={12}>
                  <Card className="public-dpp-card shadow-sm">
                    <Card.Body className="p-4">
                      <div className="text-uppercase small fw-semibold text-muted mb-3">
                        Battery Technical & Safety Data
                      </div>
                      <dl className="row mb-0">
                        {hasNumber(payload.battery.ratedCapacityAh) && (
                          <>
                            <dt className="col-sm-4">Rated Capacity</dt>
                            <dd className="col-sm-8">
                              {formatNumber(
                                payload.battery.ratedCapacityAh,
                                ' Ah',
                              )}
                            </dd>
                          </>
                        )}
                        {hasNumber(payload.battery.ratedEnergyKwh) && (
                          <>
                            <dt className="col-sm-4">Rated Energy</dt>
                            <dd className="col-sm-8">
                              {formatNumber(
                                payload.battery.ratedEnergyKwh,
                                ' kWh',
                              )}
                            </dd>
                          </>
                        )}
                        {hasNumber(payload.battery.nominalVoltageV) && (
                          <>
                            <dt className="col-sm-4">Nominal Voltage</dt>
                            <dd className="col-sm-8">
                              {formatNumber(
                                payload.battery.nominalVoltageV,
                                ' V',
                              )}
                            </dd>
                          </>
                        )}
                        {hasNumber(payload.battery.minimumVoltageV) && (
                          <>
                            <dt className="col-sm-4">Minimum Voltage</dt>
                            <dd className="col-sm-8">
                              {formatNumber(
                                payload.battery.minimumVoltageV,
                                ' V',
                              )}
                            </dd>
                          </>
                        )}
                        {hasNumber(payload.battery.maximumVoltageV) && (
                          <>
                            <dt className="col-sm-4">Maximum Voltage</dt>
                            <dd className="col-sm-8">
                              {formatNumber(
                                payload.battery.maximumVoltageV,
                                ' V',
                              )}
                            </dd>
                          </>
                        )}
                        {hasText(payload.battery.powerCapabilityLimits) && (
                          <>
                            <dt className="col-sm-4">C-rate / Power Limits</dt>
                            <dd className="col-sm-8">
                              {payload.battery.powerCapabilityLimits}
                            </dd>
                          </>
                        )}
                        {hasNumber(
                          payload.battery.roundTripEfficiencyPercent,
                        ) && (
                          <>
                            <dt className="col-sm-4">Round-trip Efficiency</dt>
                            <dd className="col-sm-8">
                              {formatNumber(
                                payload.battery.roundTripEfficiencyPercent,
                                ' %',
                              )}
                            </dd>
                          </>
                        )}
                        {hasText(payload.battery.cycleLifetimeDescriptor) && (
                          <>
                            <dt className="col-sm-4">Cycle Lifetime</dt>
                            <dd className="col-sm-8">
                              {payload.battery.cycleLifetimeDescriptor}
                            </dd>
                          </>
                        )}
                        {hasText(
                          payload.battery.operatingTemperatureWindow,
                        ) && (
                          <>
                            <dt className="col-sm-4">Temperature Window</dt>
                            <dd className="col-sm-8">
                              {payload.battery.operatingTemperatureWindow}
                            </dd>
                          </>
                        )}
                        {hasText(payload.battery.extinguishingAgents) && (
                          <>
                            <dt className="col-sm-4">Extinguishing Agents</dt>
                            <dd className="col-sm-8">
                              {payload.battery.extinguishingAgents}
                            </dd>
                          </>
                        )}
                        {hasText(payload.battery.safetyHandlingMeasures) && (
                          <>
                            <dt className="col-sm-4">Safety Handling</dt>
                            <dd className="col-sm-8">
                              {payload.battery.safetyHandlingMeasures}
                            </dd>
                          </>
                        )}
                      </dl>
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {isConstructionProduct &&
                payload.categorySpecific?.construction && (
                  <Col xs={12}>
                    <Card className="public-dpp-card shadow-sm">
                      <Card.Body className="p-4">
                        <div className="text-uppercase small fw-semibold text-muted mb-3">
                          Construction Product Data
                        </div>
                        <dl className="row mb-0">
                          {hasText(
                            payload.categorySpecific.construction
                              .vocEmissionClass,
                          ) && (
                            <>
                              <dt className="col-sm-4">VOC Emission Class</dt>
                              <dd className="col-sm-8">
                                {
                                  payload.categorySpecific.construction
                                    .vocEmissionClass
                                }
                              </dd>
                            </>
                          )}
                        </dl>
                      </Card.Body>
                    </Card>
                  </Col>
                )}

              {isFurnitureProduct && payload.categorySpecific?.furniture && (
                <Col xs={12}>
                  <Card className="public-dpp-card shadow-sm">
                    <Card.Body className="p-4">
                      {(() => {
                        const flameRetardantChemicalNames =
                          payload.categorySpecific?.furniture
                            ?.flameRetardantChemicalNames ?? [];

                        return (
                          <>
                            <div className="text-uppercase small fw-semibold text-muted mb-3">
                              Furniture Circularity & Compliance
                            </div>
                            <dl className="row mb-0">
                              {hasText(
                                payload.categorySpecific.furniture
                                  .eudrDdsReference,
                              ) && (
                                <>
                                  <dt className="col-sm-4">
                                    EUDR DDS Reference
                                  </dt>
                                  <dd className="col-sm-8">
                                    {
                                      payload.categorySpecific.furniture
                                        .eudrDdsReference
                                    }
                                  </dd>
                                </>
                              )}
                              {payload.categorySpecific.furniture
                                .hasRemovableCovers !== null && (
                                <>
                                  <dt className="col-sm-4">Removable Covers</dt>
                                  <dd className="col-sm-8">
                                    {payload.categorySpecific.furniture
                                      .hasRemovableCovers
                                      ? 'Yes'
                                      : 'No'}
                                  </dd>
                                </>
                              )}
                              <dt className="col-sm-4">
                                Flame Retardants Present
                              </dt>
                              <dd className="col-sm-8">
                                {payload.categorySpecific.furniture
                                  .flameRetardantsPresent
                                  ? 'Yes'
                                  : 'No'}
                              </dd>
                              {flameRetardantChemicalNames.length > 0 && (
                                <>
                                  <dt className="col-sm-4">
                                    Flame Retardant Chemicals
                                  </dt>
                                  <dd className="col-sm-8">
                                    {flameRetardantChemicalNames.join(', ')}
                                  </dd>
                                </>
                              )}
                            </dl>
                          </>
                        );
                      })()}
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {isMetalsProduct && payload.categorySpecific?.metals && (
                <Col xs={12}>
                  <Card className="public-dpp-card shadow-sm">
                    <Card.Body className="p-4">
                      <div className="text-uppercase small fw-semibold text-muted mb-3">
                        Metals Production Data
                      </div>
                      <dl className="row mb-0">
                        {hasText(
                          payload.categorySpecific.metals.productionRoute,
                        ) && (
                          <>
                            <dt className="col-sm-4">Production Route</dt>
                            <dd className="col-sm-8">
                              {titleCase(
                                payload.categorySpecific.metals.productionRoute,
                              )}
                            </dd>
                          </>
                        )}
                        {hasNumber(
                          payload.categorySpecific.metals.cbamEmbeddedEmissions,
                        ) && (
                          <>
                            <dt className="col-sm-4">
                              CBAM Embedded Emissions
                            </dt>
                            <dd className="col-sm-8">
                              {formatNumber(
                                payload.categorySpecific.metals
                                  .cbamEmbeddedEmissions,
                                ' tCO2e/t',
                              )}
                            </dd>
                          </>
                        )}
                      </dl>
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {isTiresProduct && payload.categorySpecific?.tires && (
                <Col xs={12}>
                  <Card className="public-dpp-card shadow-sm">
                    <Card.Body className="p-4">
                      <div className="text-uppercase small fw-semibold text-muted mb-3">
                        Tire Performance Data
                      </div>
                      <dl className="row mb-0">
                        {hasText(
                          payload.categorySpecific.tires.rollingResistanceClass,
                        ) && (
                          <>
                            <dt className="col-sm-4">
                              Rolling Resistance Class
                            </dt>
                            <dd className="col-sm-8">
                              {
                                payload.categorySpecific.tires
                                  .rollingResistanceClass
                              }
                            </dd>
                          </>
                        )}
                        {hasText(
                          payload.categorySpecific.tires.wetGripClass,
                        ) && (
                          <>
                            <dt className="col-sm-4">Wet Grip Class</dt>
                            <dd className="col-sm-8">
                              {payload.categorySpecific.tires.wetGripClass}
                            </dd>
                          </>
                        )}
                        {hasNumber(
                          payload.categorySpecific.tires.abrasionRateMgKm,
                        ) && (
                          <>
                            <dt className="col-sm-4">Abrasion Rate</dt>
                            <dd className="col-sm-8">
                              {formatNumber(
                                payload.categorySpecific.tires.abrasionRateMgKm,
                                ' mg/km',
                              )}
                            </dd>
                          </>
                        )}
                        {payload.categorySpecific.tires.isRetreadable !==
                          null && (
                          <>
                            <dt className="col-sm-4">Retreadable</dt>
                            <dd className="col-sm-8">
                              {payload.categorySpecific.tires.isRetreadable
                                ? 'Yes'
                                : 'No'}
                            </dd>
                          </>
                        )}
                      </dl>
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {isChemicalsProduct && payload.categorySpecific?.chemicals && (
                <Col xs={12}>
                  <Card className="public-dpp-card shadow-sm">
                    <Card.Body className="p-4">
                      {(() => {
                        const clpHazardPhrases =
                          payload.categorySpecific?.chemicals
                            ?.clpHazardPhrases ?? [];

                        return (
                          <>
                            <div className="text-uppercase small fw-semibold text-muted mb-3">
                              Chemical Compliance Data
                            </div>
                            <dl className="row mb-0">
                              {hasText(
                                payload.categorySpecific.chemicals.ufiCode,
                              ) && (
                                <>
                                  <dt className="col-sm-4">UFI Code</dt>
                                  <dd className="col-sm-8">
                                    {payload.categorySpecific.chemicals.ufiCode}
                                  </dd>
                                </>
                              )}
                              {clpHazardPhrases.length > 0 && (
                                <>
                                  <dt className="col-sm-4">
                                    CLP Hazard Phrases
                                  </dt>
                                  <dd className="col-sm-8">
                                    {clpHazardPhrases.join(', ')}
                                  </dd>
                                </>
                              )}
                              {hasNumber(
                                payload.categorySpecific.chemicals
                                  .surfactantBiodegradabilityPercentage,
                              ) && (
                                <>
                                  <dt className="col-sm-4">
                                    Surfactant Biodegradability
                                  </dt>
                                  <dd className="col-sm-8">
                                    {formatNumber(
                                      payload.categorySpecific.chemicals
                                        .surfactantBiodegradabilityPercentage,
                                      ' %',
                                    )}
                                  </dd>
                                </>
                              )}
                            </dl>
                          </>
                        );
                      })()}
                    </Card.Body>
                  </Card>
                </Col>
              )}

              <Col lg={4}>
                <Card className="public-dpp-card shadow-sm h-100">
                  <Card.Body className="p-4">
                    <div className="text-uppercase small fw-semibold text-muted mb-3">
                      REACH
                    </div>
                    <div className="mb-3">
                      <div className="small text-muted">SVHC Status</div>
                      <div className="fw-semibold">
                        {titleCase(payload.reach.svhcStatus || 'not_declared')}
                      </div>
                    </div>
                    {payload.reach.svhcDeclarations.length > 0 ? (
                      <div className="d-flex flex-column gap-3">
                        {payload.reach.svhcDeclarations.map((item, index) => (
                          <div key={`${item.substanceName}-${index}`}>
                            <div className="fw-semibold">
                              {item.substanceName || '-'}
                            </div>
                            <div className="small text-muted">
                              {item.casEcNumber || '-'}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted">
                        No SVHC entries published.
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4}>
                <Card className="public-dpp-card shadow-sm h-100">
                  <Card.Body className="p-4">
                    <div className="text-uppercase small fw-semibold text-muted mb-3">
                      RoHS
                    </div>
                    <div className="fw-semibold mb-3">
                      {getRohsStatusLabel(payload.rohs.rohsStatus)}
                    </div>
                    {payload.rohs.rohsStatus === 'with_exemption' && (
                      <dl className="row mb-0 small">
                        <dt className="col-sm-5">Exemption</dt>
                        <dd className="col-sm-7">
                          {payload.rohs.exemptionCode === 'OTHER'
                            ? payload.rohs.customExemptionDetails || 'Other'
                            : payload.rohs.exemptionCode || '-'}
                        </dd>
                        {payload.rohs.exemptionCode === 'OTHER' &&
                          payload.rohs.customExemptionDetails && (
                            <>
                              <dt className="col-sm-5">Details</dt>
                              <dd className="col-sm-7">
                                {payload.rohs.customExemptionDetails}
                              </dd>
                            </>
                          )}
                      </dl>
                    )}
                    {payload.rohs.rohsStatus === 'not_applicable' && (
                      <dl className="row mb-0 small">
                        <dt className="col-sm-5">Reason</dt>
                        <dd className="col-sm-7">
                          {payload.rohs.exclusionReason || '-'}
                        </dd>
                      </dl>
                    )}
                    {technicalSpecsVisible &&
                      payload.rohs.supportingDocument && (
                        <div className="mt-3">
                          <div className="small text-muted mb-1">
                            Supporting Document
                          </div>
                          {payload.rohs.supportingDocument.url ? (
                            <a
                              href={payload.rohs.supportingDocument.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {payload.rohs.supportingDocument.title ||
                                'Open RoHS Supporting Document'}
                            </a>
                          ) : (
                            <Badge bg="success">
                              <i className="bi bi-check-lg me-1" />
                              Document Verified
                            </Badge>
                          )}
                        </div>
                      )}
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4}>
                <Card className="public-dpp-card shadow-sm h-100">
                  <Card.Body className="p-4">
                    <div className="text-uppercase small fw-semibold text-muted mb-3">
                      POPs & PFAS
                    </div>
                    <div className="fw-semibold mb-3">
                      {isBatteryProduct
                        ? getBatteryPfasStatusLabel(payload.popsPfas)
                        : popsPfasAppliesToProduct || payload.popsPfas.status
                          ? getPopsPfasStatusLabel(payload.popsPfas.status)
                          : 'Not applicable'}
                    </div>
                    {isBatteryProduct ? (
                      <>
                        <div className="small text-muted mb-1">PFAS Status</div>
                        <div>{getBatteryPfasStatusLabel(payload.popsPfas)}</div>
                        {hasText(payload.popsPfas.assessmentNote) && (
                          <div className="text-muted mt-3">
                            {payload.popsPfas.assessmentNote}
                          </div>
                        )}
                        {payload.popsPfas.supportingDocument && (
                          <div className="mt-3">
                            <div className="small text-muted mb-1">
                              Supporting Document
                            </div>
                            {payload.popsPfas.supportingDocument.url ? (
                              <a
                                href={payload.popsPfas.supportingDocument.url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {payload.popsPfas.supportingDocument.title ||
                                  'Open POPs/PFAS Supporting Document'}
                              </a>
                            ) : (
                              <Badge bg="success">
                                <i className="bi bi-check-lg me-1" />
                                Document Verified
                              </Badge>
                            )}
                          </div>
                        )}
                      </>
                    ) : popsPfasAppliesToProduct ? (
                      payload.popsPfas.status === 'contains_restricted' ? (
                        <>
                          <div className="small text-muted mb-1">
                            Declared substance / exemption details
                          </div>
                          <div>{payload.popsPfas.details || '-'}</div>
                        </>
                      ) : payload.popsPfas.status === 'verified_compliant' ? (
                        <>
                          <div className="text-muted">
                            No prohibited POPs or PFAS have been declared for
                            this product.
                          </div>
                          {payload.popsPfas.supportingDocument && (
                            <div className="mt-3">
                              <div className="small text-muted mb-1">
                                Supporting Document
                              </div>
                              {payload.popsPfas.supportingDocument.url ? (
                                <a
                                  href={payload.popsPfas.supportingDocument.url}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {payload.popsPfas.supportingDocument.title ||
                                    'Open POPs/PFAS Supporting Document'}
                                </a>
                              ) : (
                                <Badge bg="success">
                                  <i className="bi bi-check-lg me-1" />
                                  Document Verified
                                </Badge>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-muted">
                          No verified POPs / PFAS declaration has been published
                          yet.
                        </div>
                      )
                    ) : (
                      <div className="text-muted">
                        No POPs / PFAS declaration has been published for this
                        product category.
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </>
          ) : (
            <Col xs={12}>
              <Card className="public-dpp-card shadow-sm">
                <Card.Body className="p-4 p-lg-5">
                  <div className="text-uppercase small fw-semibold text-muted mb-3">
                    Technical Specifications
                  </div>
                  <h2 className="h5 mb-3">Restricted access required</h2>
                  <p className="text-muted mb-0">
                    This passport is configured to keep materials, compliance,
                    packaging, and circularity specifications behind restricted
                    access.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          )}

          {complianceDocuments.length > 0 && (
            <Col xs={12}>
              <Card className="public-dpp-card shadow-sm">
                <Card.Body className="p-4">
                  <div className="text-uppercase small fw-semibold text-muted mb-1">
                    Compliance Documents
                  </div>
                  <div className="d-flex flex-column gap-3">
                    {complianceDocuments.map((document) => {
                      return (
                        <div key={document.key} className="border rounded p-3">
                          <div className="d-flex align-items-start gap-3">
                            <div
                              className="bg-light border rounded d-inline-flex align-items-center justify-content-center flex-shrink-0"
                              style={{ width: '44px', height: '44px' }}
                            >
                              <i className="bi bi-file-earmark-check text-secondary" />
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                <div className="fw-semibold">
                                  {document.title}
                                </div>
                              </div>
                              <div className="text-muted small mb-2">
                                {document.description}
                              </div>
                              {document.url ? (
                                <a
                                  href={document.url}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Open document
                                </a>
                              ) : (
                                <div className="small text-muted">
                                  Document is not available for the current
                                  access tier.
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          )}

          {publicDocuments.length > 0 && (
            <Col xs={12}>
              <Card className="public-dpp-card shadow-sm">
                <Card.Body className="p-4">
                  <div className="text-uppercase small fw-semibold text-muted mb-3">
                    Additional Documents
                  </div>
                  <div className="d-flex flex-column gap-3">
                    {publicDocuments.map((document, index) => (
                      <div
                        key={`${document.title}-${document.url ?? index}`}
                        className="border rounded p-3"
                      >
                        <div className="d-flex align-items-start gap-3 flex-grow-1">
                          <div
                            className="bg-light border rounded d-inline-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: '44px', height: '44px' }}
                          >
                            <i className="bi bi-file-earmark-text text-secondary" />
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                              <div className="fw-semibold">
                                {document.title}
                              </div>
                              {document.category && (
                                <Badge
                                  bg="light"
                                  text="dark"
                                  className="border"
                                >
                                  {document.category}
                                </Badge>
                              )}
                            </div>
                            {document.description && (
                              <div className="text-muted small mb-2">
                                {document.description}
                              </div>
                            )}
                            {document.uploadedAt && (
                              <div className="small text-muted">
                                Uploaded {formatDate(document.uploadedAt)}
                              </div>
                            )}
                            {document.url && (
                              <div className="small mt-2">
                                <a
                                  href={document.url}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Open document
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          )}

          {hasExtendedAccess && payload.extendedData ? (
            <>
              <Col xs={12}>
                <Card className="public-dpp-card shadow-sm">
                  <Card.Body className="p-4">
                    <div className="d-flex flex-wrap justify-content-between gap-3 mb-3">
                      <div className="text-uppercase small fw-semibold text-muted">
                        {isConfidentialTier
                          ? 'Confidential Access Data'
                          : 'Restricted Access Data'}
                      </div>
                      <Badge
                        bg={isConfidentialTier ? 'danger' : 'warning'}
                        text={isConfidentialTier ? undefined : 'dark'}
                      >
                        {grantedTier}
                      </Badge>
                    </div>
                    <div className="text-muted mb-4">
                      {isConfidentialTier
                        ? 'Level 3 access exposes complete unredacted traceability and full compliance identifiers for authorities and internal administrators.'
                        : 'Level 2 access adds circularity and compliance details while masking supplier identity and facility location.'}
                    </div>
                    <div className="d-flex flex-column gap-4">
                      <div>
                        <div className="small text-muted text-uppercase fw-semibold mb-2">
                          Supply Chain Traceability
                        </div>
                        {payload.extendedData.traceability.length > 0 ? (
                          <Table responsive className="mb-0 align-middle">
                            <thead>
                              <tr>
                                <th>Tier</th>
                                <th>Company</th>
                                <th>Country</th>
                                <th>Facility ID / Address</th>
                                <th>Process / Stage</th>
                              </tr>
                            </thead>
                            <tbody>
                              {payload.extendedData.traceability.map(
                                (row, index) => (
                                  <tr
                                    key={`${row.tier}-${row.companyName}-${index}`}
                                  >
                                    <td>{titleCase(row.tier)}</td>
                                    <td>{row.companyName || '-'}</td>
                                    <td>{row.countryCode || '-'}</td>
                                    <td>{row.facilityIdOrAddress || '-'}</td>
                                    <td>{row.processComponent || '-'}</td>
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </Table>
                        ) : (
                          <div className="text-muted">
                            No extended traceability rows are available for this
                            access tier.
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="small text-muted text-uppercase fw-semibold mb-2">
                          Circularity Additions
                        </div>
                        <dl className="row mb-0">
                          <dt className="col-sm-6">Critical Raw Materials</dt>
                          <dd className="col-sm-6">
                            {payload.extendedData.compliance
                              .criticalRawMaterials
                              ? 'Yes'
                              : 'No'}
                          </dd>
                          <dt className="col-sm-6">
                            Critical Raw Materials List
                          </dt>
                          <dd className="col-sm-6">
                            {payload.extendedData.compliance
                              .criticalRawMaterialsList.length > 0
                              ? payload.extendedData.compliance.criticalRawMaterialsList.join(
                                  ', ',
                                )
                              : '-'}
                          </dd>
                          <dt className="col-sm-6">Repairability Score</dt>
                          <dd className="col-sm-6">
                            {hasNumber(payload.circularity.repairabilityScore)
                              ? formatNumber(
                                  payload.circularity.repairabilityScore,
                                  ' / 10',
                                )
                              : '-'}
                          </dd>
                          <dt className="col-sm-6">Spare Parts Availability</dt>
                          <dd className="col-sm-6">
                            {payload.circularity.sparePartsAvailability || '-'}
                          </dd>
                          <dt className="col-sm-6">Repair Manual</dt>
                          <dd className="col-sm-6">
                            {payload.circularity.repairManual
                              ? 'Available'
                              : 'Not published'}
                          </dd>
                          <dt className="col-sm-6">Disassembly Guide</dt>
                          <dd className="col-sm-6">
                            {payload.circularity.disassemblyGuide
                              ? 'Available'
                              : 'Not published'}
                          </dd>
                        </dl>
                      </div>
                      <div>
                        <div className="small text-muted text-uppercase fw-semibold mb-2">
                          Compliance Identifiers
                        </div>
                        <dl className="row mb-0">
                          <dt className="col-sm-6">WEEE Registration</dt>
                          <dd className="col-sm-6">
                            {payload.extendedData.compliance
                              .weeeRegistrationNumber || '-'}
                          </dd>
                          {isConfidentialTier && (
                            <>
                              <dt className="col-sm-6">Importer EORI</dt>
                              <dd className="col-sm-6">
                                {payload.extendedData.compliance.importerEori ||
                                  '-'}
                              </dd>
                              <dt className="col-sm-6">Hardware Revision</dt>
                              <dd className="col-sm-6">
                                {payload.extendedData.compliance
                                  .hardwareRevision || '-'}
                              </dd>
                              {hasText(
                                payload.extendedData.compliance
                                  .rohsExemptionCode,
                              ) && (
                                <>
                                  <dt className="col-sm-6">
                                    RoHS Exemption Code
                                  </dt>
                                  <dd className="col-sm-6">
                                    {
                                      payload.extendedData.compliance
                                        .rohsExemptionCode
                                    }
                                  </dd>
                                </>
                              )}
                              {hasText(
                                payload.extendedData.compliance
                                  .customExemptionDetails,
                              ) && (
                                <>
                                  <dt className="col-sm-6">
                                    RoHS Custom Exemption Details
                                  </dt>
                                  <dd className="col-sm-6">
                                    {
                                      payload.extendedData.compliance
                                        .customExemptionDetails
                                    }
                                  </dd>
                                </>
                              )}
                              {hasText(
                                payload.extendedData.compliance
                                  .rohsExclusionReason,
                              ) && (
                                <>
                                  <dt className="col-sm-6">
                                    RoHS Exclusion Reason
                                  </dt>
                                  <dd className="col-sm-6">
                                    {
                                      payload.extendedData.compliance
                                        .rohsExclusionReason
                                    }
                                  </dd>
                                </>
                              )}
                            </>
                          )}
                        </dl>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </>
          ) : null}
        </Row>
      )}

      {!isWithdrawn && (
        <footer className="mt-4 pt-2">
          <Card className="public-dpp-card shadow-sm">
            <Card.Body className="p-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div>
                {hasExtendedAccess
                  ? `Extended ${grantedTier.toLowerCase()} access is active for this Digital Product Passport.`
                  : 'You are viewing public information about the product.'}
              </div>
              {!hasExtendedAccess && (
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={() => {
                    setAccessRequestError(null);
                    setShowAccessRequestModal(true);
                  }}
                >
                  🔒 Request Restricted / Auditor Access
                </Button>
              )}
            </Card.Body>
          </Card>
        </footer>
      )}

      <Modal
        show={showAccessRequestModal}
        onHide={closeAccessRequestModal}
        centered
      >
        <Modal.Header closeButton={!accessRequestSubmitting}>
          <Modal.Title>Request Restricted / Auditor Access</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Full Name</Form.Label>
            <Form.Control
              value={accessRequestForm.fullName}
              onChange={(event) =>
                updateAccessRequestForm('fullName', event.target.value)
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={accessRequestForm.email}
              onChange={(event) =>
                updateAccessRequestForm('email', event.target.value)
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Organization</Form.Label>
            <Form.Control
              value={accessRequestForm.organization}
              onChange={(event) =>
                updateAccessRequestForm('organization', event.target.value)
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Role</Form.Label>
            <Form.Select
              value={accessRequestForm.role}
              onChange={(event) =>
                updateAccessRequestForm(
                  'role',
                  event.target.value as AccessRequestRole,
                )
              }
            >
              <option value="RECYCLER_REPAIRER">Recycler / Repairer</option>
              <option value="AUDITOR_SURVEILLANCE">
                Auditor / Surveillance Authority
              </option>
              <option value="B2B_BUYER">B2B Buyer</option>
              <option value="OTHER">Other</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Reason</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={accessRequestForm.reason}
              onChange={(event) =>
                updateAccessRequestForm('reason', event.target.value)
              }
            />
          </Form.Group>
          {accessRequestError && (
            <Alert variant="danger" className="mt-3 mb-0">
              {accessRequestError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeAccessRequestModal}
            disabled={accessRequestSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="dark"
            onClick={() => void handleSubmitAccessRequest()}
            disabled={
              accessRequestSubmitting ||
              !accessRequestForm.fullName.trim() ||
              !accessRequestForm.email.trim() ||
              !accessRequestForm.organization.trim() ||
              !accessRequestForm.role ||
              !accessRequestForm.reason.trim()
            }
          >
            {accessRequestSubmitting ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Submitting...
              </>
            ) : (
              'Submit Request'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
